import React, { useState, useEffect } from 'react';
import { X, PhoneCall, ArrowRight } from 'lucide-react';

const HOOKS = [
  {
    title: 'क्या शादी में हो रही है देरी?',
    titleEn: 'Marriage Getting Delayed?',
    desc: 'कुंडली में विवाह योग कमजोर हो सकता है। सही उपाय से जल्द विवाह संभव है।',
    cta: 'अभी परामर्श लें',
    icon: '💍'
  },
  {
    title: 'करियर में नहीं मिल रही सफलता?',
    titleEn: 'Struggling in Career?',
    desc: 'ग्रहों की चाल आपके करियर को प्रभावित कर रही हो सकती है। जानिए सही राह।',
    cta: 'करियर गाइडेंस लें',
    icon: '📈'
  },
  {
    title: 'पैसों की तंगी से परेशान हैं?',
    titleEn: 'Financial Problems?',
    desc: 'धन योग कमजोर होने पर आर्थिक समस्याएं आती हैं। ज्योतिषीय उपाय से लाभ संभव।',
    cta: 'धन समस्या का समाधान',
    icon: '💰'
  },
  {
    title: 'पति-पत्नी में अनबन?',
    titleEn: 'Marriage Issues?',
    desc: 'वैवाहिक कलह के पीछे ग्रह दोष हो सकता है। सही मार्गदर्शन से सुखी जीवन।',
    cta: 'वैवाहिक समस्या हल करें',
    icon: '❤️'
  },
  {
    title: 'संतान सुख नहीं मिल रहा?',
    titleEn: 'Child-Related Issues?',
    desc: 'पांचवें भाव में दोष हो सकता है। ज्योतिषीय उपाय से संतान प्राप्ति संभव।',
    cta: 'संतान योग जानें',
    icon: '👶'
  },
  {
    title: 'स्वास्थ्य समस्या बार-बार?',
    titleEn: 'Recurring Health Issues?',
    desc: 'ग्रहों का अशुभ प्रभाव स्वास्थ्य पर पड़ सकता है। ज्योतिषीय उपचार से राहत पाएं।',
    cta: 'स्वास्थ्य उपाय जानें',
    icon: '🏥'
  }
];

export default function AttentionPopup() {
  const [show, setShow] = useState(false);
  const [hookIndex, setHookIndex] = useState(0);

  useEffect(() => {
    // Show first popup after 5 seconds
    const initialTimer = setTimeout(() => {
      setHookIndex(Math.floor(Math.random() * HOOKS.length));
      setShow(true);
    }, 5000);

    return () => clearTimeout(initialTimer);
  }, []);

  // After close, reappear after 1 min 30 sec (90 seconds)
  useEffect(() => {
    if (!show) {
      const reappear = setTimeout(() => {
        setHookIndex(Math.floor(Math.random() * HOOKS.length));
        setShow(true);
      }, 90000);
      return () => clearTimeout(reappear);
    }
  }, [show]);

  const close = () => {
    setShow(false);
  };

  if (!show) return null;

  const hook = HOOKS[hookIndex];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="attention-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />

      {/* Popup */}
      <div className="relative w-full max-w-md animate-[scaleIn_0.3s_ease-out]" style={{ animation: 'scaleIn 0.3s ease-out' }}>
        <div className="relative bg-gradient-to-br from-[#3d101e] to-[#2c0b14] rounded-2xl border border-gold/20 shadow-2xl shadow-gold/10 overflow-hidden">
          {/* Gold top strip */}
          <div className="h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-light" />

          {/* Close button */}
          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
            data-testid="popup-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="p-6 md:p-8 text-center">
            {/* Icon */}
            <div className="text-5xl mb-4">{hook.icon}</div>

            {/* Hook */}
            <h3 className="font-heading text-2xl md:text-3xl text-gold font-medium mb-2" data-testid="popup-title">
              {hook.title}
            </h3>
            <p className="text-text-muted text-xs uppercase tracking-wider mb-3">{hook.titleEn}</p>

            {/* Description */}
            <p className="text-text-muted text-sm leading-relaxed mb-6">
              {hook.desc}
            </p>

            {/* Expert info */}
            <div className="flex items-center justify-center gap-3 mb-6 bg-white/5 rounded-xl p-3">
              <img
                src="/media/founder/IMG_9620-removebg-preview.png"
                alt="Vivek Mudgal Astrologer"
                className="w-12 h-12 rounded-full object-cover object-top border-2 border-gold/30"
              />
              <div className="text-left">
                <p className="text-white text-sm font-medium">Vivek Mudgal Astrologer</p>
                <p className="text-gold/60 text-xs">33+ Years Experience | 33,000+ Clients</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#book-consultation"
                onClick={close}
                className="btn-gold flex-1 flex items-center justify-center gap-2 text-sm"
                data-testid="popup-book-btn"
              >
                {hook.cta} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://wa.me/919311369729?text=नमस्ते%20पंडित%20जी,%20मुझे%20परामर्श%20चाहिए"
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="btn-gold-outline flex-1 flex items-center justify-center gap-2 text-sm"
                data-testid="popup-whatsapp-btn"
              >
                <PhoneCall className="w-4 h-4" /> WhatsApp करें
              </a>
            </div>

            {/* Trust badge */}
            <p className="text-gold/40 text-[10px] mt-4 uppercase tracking-wider">
              Acharya Vivek Mudgal | वैदिक ज्योतिष परामर्श
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
